import {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import axios, { AxiosResponse } from 'axios'

import { useJHireURL } from '../../../contexts/Domain'

export interface ApplicationListProps {
    candidateId: string
}
export default function useApplicationList({
    candidateId,
}: ApplicationListProps) {
    const JHIRE_URL = useJHireURL()

    const mountedRef = useRef(true)
    let { current: mounted } = mountedRef
    const networkCounterRef = useRef(0)

    const [fetching, setFetching] = useState(true)
    const [error, setError] = useState('')
    const [data, setData] = useState<CandidateApplicationListResults>({
        nodes: [],
        totalCount: 0,
    })

    const fetchApplications = () => {
        setFetching(true)
        setError('')
        const currentCount = networkCounterRef.current + 1
        networkCounterRef.current += 1

        try {
            const includeMatchInfo = Boolean(window.properties.isCandidateRankingEnabled)
            axios.get(`${JHIRE_URL}/action/application/${candidateId}/applications?allApplications=true&includeMatchInfo=${includeMatchInfo}&readMode=1`)
                .then((response: AxiosResponse<GetAllApplicationsResponse>) => {
                    if (mounted && currentCount === networkCounterRef.current) {
                        if (includeMatchInfo) {
                            const rankScoreMap: any = {}

                            if (response.data.rankScores && response.data.rankScores.length > 0) {
                                for(const rankScore of response.data.rankScores) {
                                    rankScoreMap[rankScore.applicationEId] = rankScore.overall.score
                                }
                            }
                            
                            for(const application of response.data.applications) {
                                const rankScore = rankScoreMap[application.EId]
                                
                                if (rankScore) {
                                    application.rankScore = rankScore
                                }
                            }
                        }
                            
                        setData({
                            nodes: response.data.applications,
                            totalCount: response.data.applications.length,
                        })
                        setFetching(false)
                        setError('')
                    }
                })
                .catch((error) => {
                    if (mounted && currentCount === networkCounterRef.current) {
                        setFetching(false)
                        setError('An error occurred while fetching applications')
                    }
                    console.log(error)
                })
        } catch(error) {
            if (mounted && currentCount === networkCounterRef.current) {
                setFetching(false)
                setError('An error occurred while fetching applications')
            }
            console.log(error)
        }
    }

    useEffect(() => {
        if (mounted && candidateId) {
            fetchApplications()
        }
    }, [candidateId])

    useEffect(() => {
        mounted = true

        return () => {
            mounted = false
        }
    }, [])

    return useMemo(() => ({
        data: data,
        isLoading: fetching,
        error,
    }), [
        data,
        fetching,
        error,
    ])
}

export interface Application {
    EId: string
    active: boolean
    applicationStatus: string
    applicationStatusKind: string
    candidate: {
        EId: string
        email: string
        firstName: string
        lastName: string
    }
    candidateEId: string
    job: {
        EId: string
        requisitionId: string
        title: string
    }
    lastUpdate: number
    modified: number
    sentDate: number
    workflowIndicators: {
        [key: string]: any
    }
    rankScore?: number
}

export interface RankScoreApplication {
    applicationEId: string
    overall: {
        score: number
    }
}
export interface GetAllApplicationsResponse {
    applications: Array<Application>
    rankScores?: Array<RankScoreApplication>
}

export interface CandidateApplicationListResults {
    nodes: Array<Application>
    totalCount: number
}
